import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { NOTIFICATIONS_QUERY_KEY } from '@/hooks/queries/notifications/use-notification-meta';
import {
  createNotificationMessage,
  getNotificationMessages,
  mapNotificationMessagesResponse,
  type NotificationMessageContentRequest,
  patchRawListRowMessage,
  prependRawListRowMessage,
  type RawNotificationMessage,
  type RawNotificationMessagesResponse,
  type ReviewMessageRequest,
  reviewNotificationMessage,
  sendNotificationMessage,
  updateNotificationMessage,
} from '@/services/api/notifications';

export const NOTIFICATION_MESSAGE_LISTS_QUERY_KEY = [...NOTIFICATIONS_QUERY_KEY, 'messages', 'list'] as const;

const LIST_STALE_TIME_MS = 30 * 1000;

export const notificationMessagesQueryOptions = (cursor?: string) =>
  queryOptions({
    queryKey: [...NOTIFICATION_MESSAGE_LISTS_QUERY_KEY, { cursor }],
    queryFn: ({ signal }) => getNotificationMessages(cursor, signal),
    select: mapNotificationMessagesResponse,
    // Approvals move under you (another user submits, legal approves) and the global
    // defaults never refocus-refetch - override so returning to the tab shows fresh state
    staleTime: LIST_STALE_TIME_MS,
    refetchOnWindowFocus: true,
  });

export const useNotificationMessages = (cursor?: string) => useQuery(notificationMessagesQueryOptions(cursor));

// Writes patch cached list pages from the response (every write returns the full message with
// the caller's allowed_actions), then mark lists stale WITHOUT refetching - the next natural
// refetch (focus, remount) confirms
const patchListsAfterWrite = async (queryClient: ReturnType<typeof useQueryClient>, message: RawNotificationMessage) => {
  queryClient.setQueriesData<RawNotificationMessagesResponse>({ queryKey: NOTIFICATION_MESSAGE_LISTS_QUERY_KEY }, (raw) => patchRawListRowMessage(raw, message));
  await queryClient.invalidateQueries({ queryKey: NOTIFICATION_MESSAGE_LISTS_QUERY_KEY, refetchType: 'none' });
};

export const useCreateNotificationMessage = () => {
  const queryClient = useQueryClient();
  return async (body: NotificationMessageContentRequest): Promise<RawNotificationMessage> => {
    const message = await createNotificationMessage(body);

    // New drafts land on page 1 (newest first) - prepend rather than waiting for a refetch
    queryClient.setQueriesData<RawNotificationMessagesResponse>({ queryKey: [...NOTIFICATION_MESSAGE_LISTS_QUERY_KEY, { cursor: undefined }] }, (raw) =>
      prependRawListRowMessage(raw, message),
    );
    await queryClient.invalidateQueries({ queryKey: NOTIFICATION_MESSAGE_LISTS_QUERY_KEY, refetchType: 'none' });
    return message;
  };
};

export const useUpdateNotificationMessage = () => {
  const queryClient = useQueryClient();
  return async (messageId: string, body: NotificationMessageContentRequest): Promise<RawNotificationMessage> => {
    const message = await updateNotificationMessage(messageId, body);
    await patchListsAfterWrite(queryClient, message);
    return message;
  };
};

export const useReviewNotificationMessage = () => {
  const queryClient = useQueryClient();
  return async (messageId: string, body: ReviewMessageRequest): Promise<RawNotificationMessage> => {
    const message = await reviewNotificationMessage(messageId, body);
    await patchListsAfterWrite(queryClient, message);
    return message;
  };
};

export const useSendNotificationMessage = () => {
  const queryClient = useQueryClient();
  return async (messageId: string, acknowledgeNoLegalReview: boolean): Promise<RawNotificationMessage> => {
    const message = await sendNotificationMessage(messageId, acknowledgeNoLegalReview);
    await patchListsAfterWrite(queryClient, message);
    return message;
  };
};
